import { getApiOrigin, resolveMediaUrl } from "./media";

/**
 * Collect PDF rows from one API object (public `pdfs`, PascalCase, `pdfDocuments`, etc.).
 */
function collectPdfRowsFromDto(apiDto) {
  if (!apiDto || typeof apiDto !== "object") return [];
  const o = apiDto;
  const out = [];

  const pushArray = (arr) => {
    if (!Array.isArray(arr)) return;
    for (const p of arr) {
      if (!p) continue;
      const url = String(p?.fileUrl ?? p?.FileUrl ?? p?.url ?? p?.Url ?? p?.path ?? "").trim();
      if (!url) continue;
      if (p?.isActive === false) continue;
      out.push(p);
    }
  };

  pushArray(o.pdfs);
  pushArray(o.Pdfs);

  const docs = o.pdfDocuments ?? o.PdfDocuments;
  if (Array.isArray(docs)) {
    for (const row of docs) {
      if (!row) continue;
      if (row?.isActive === false) continue;
      const url = String(row?.fileUrl ?? row?.FileUrl ?? row?.url ?? row?.Url ?? "").trim();
      if (!url) continue;
      out.push(row);
    }
  }

  return out;
}

/**
 * Merge PDF rows from multiple responses (detail + list row + fallback).
 * De-duplicates by `id` when present; prefers rows that have a file URL.
 */
export function pickPublicPdfArray(...sources) {
  const collected = [];
  for (const src of sources) {
    collected.push(...collectPdfRowsFromDto(src));
  }

  const byId = new Map();
  const noId = [];

  for (const p of collected) {
    const id = p?.id ?? p?.Id;
    const url = String(p?.fileUrl ?? p?.FileUrl ?? p?.url ?? p?.Url ?? "").trim();
    if (!url) continue;

    if (id === undefined || id === null) {
      noId.push(p);
      continue;
    }

    const prev = byId.get(id);
    if (!prev) {
      byId.set(id, p);
      continue;
    }
    const prevUrl = String(prev?.fileUrl ?? prev?.FileUrl ?? "").trim();
    if (!prevUrl && url) byId.set(id, p);
  }

  return [...byId.values(), ...noId];
}

/**
 * Prefer PDFs whose `productSlug` matches the URL slug / accessoryKey (case-insensitive).
 * Rows with no `productSlug` still pass (legacy).
 * If filtering removes everything but the detail API returned rows, keep those rows — GET /accessories/{id}
 * is already scoped to one accessory, so we avoid hiding all PDFs when slug casing or backend values disagree.
 */
export function filterPdfRowsByProductSlug(rows, ...slugHints) {
  const hints = slugHints
    .flat()
    .map((s) => String(s ?? "").trim())
    .filter(Boolean);
  const list = Array.isArray(rows) ? rows : [];
  if (!hints.length) return list;

  const hintLc = new Set(hints.map((h) => h.toLowerCase()));

  const filtered = list.filter((p) => {
    const ps = String(p?.productSlug ?? p?.ProductSlug ?? "").trim();
    if (!ps) return true;
    return hintLc.has(ps.toLowerCase());
  });

  if (filtered.length === 0 && list.length > 0) {
    return list;
  }
  return filtered;
}

/**
 * Maps accessory/metal-frame PDF rows to UI links.
 * Handles camelCase / PascalCase from JSON and relative `/uploads/...` paths.
 */
export function mapPublicProductPdfs(rawList) {
  const list = Array.isArray(rawList) ? rawList : [];
  return list
    .map((p, idx) => {
      const id = p?.id ?? p?.Id ?? idx + 1;
      const title = (p?.title ?? p?.Title ?? "Document").trim() || "Document";
      const pathRaw =
        p?.fileUrl ??
        p?.FileUrl ??
        p?.file_url ??
        p?.url ??
        p?.Url ??
        p?.path ??
        "";
      let href = resolveMediaUrl(pathRaw);
      if (!href && typeof pathRaw === "string" && pathRaw.trim()) {
        const t = pathRaw.trim().replace(/\\/g, "/");
        if (t.startsWith("/")) {
          href = `${getApiOrigin()}${t}`;
        }
      }
      return { id, title, href };
    })
    .filter((x) => Boolean(x.href));
}
